import { defineEnv } from '@abax/platform/env';
import { z } from 'zod';

export default defineEnv({
  API_BASE_PATH: z.string().default(''),
  AUTH_CLIENT_ID: z.string().min(1),
  AUTH_AUTHORITY: z.string().url(),
  // Hostname of the Automile-branded deployment (e.g. automile.stg.gcp.abax.services). On that
  // host the `*_AUTOMILE` keys replace their defaults. Empty disables brand switching.
  AUTOMILE_HOST: z.string().default(''),
  // Read only through getAuthority(), which covers every redirect to the identity server:
  // sign-in, silent renew, sign-out, account switching and api.auth.getAuthority().
  AUTH_AUTHORITY_AUTOMILE: z.string().default(''),
  AUTH_API_SCOPES: z.string().min(1),
  LOG_TO_DATADOG: z.boolean().default(false),
  DATADOG_CLIENT_KEY: z.string().default(''),
  DATADOG_SITE: z.string().default('datadoghq.eu'),
  DATADOG_IGNORE_URLS: z.array(z.string()).default([]),
  // Any string: deploy scopes set more names than these (review, local), and every reader
  // already handles an unknown one.
  ENVIRONMENT: z.string().default('development'),
  SHELL_BACKEND_URL: z.string().default(''),
  SHELL_BACKEND_EXTERNAL_URL: z.string().default(''),
  DEFAULT_SIGN_OUT_REDIRECT_URL: z.string().url(),
  // Read only through getDefaultSignOutRedirectUrl().
  DEFAULT_SIGN_OUT_REDIRECT_URL_AUTOMILE: z.string().default(''),
  VERSION_CHECK_INTERVAL: z.number().int().nonnegative().default(300),
  NEW_VERSION_RELOAD_COUNTDOWN: z.number().int().nonnegative().default(43200),
  IMPORTANT_NEW_VERSION_RELOAD_COUNTDOWN: z.number().int().nonnegative().default(300),
  SYSTEM_UPDATE_NOTICE_THRESHOLD: z.number().int().nonnegative().default(3600),
  SYSTEM_UPDATE_NOTICE_COUNTDOWN_THRESHOLD: z.number().int().nonnegative().default(300),
  ENABLE_CUSTOMER_SUPPORT_CHAT: z.boolean().default(true),
  GTM_KEY: z.string().default(''),
  ENABLE_GTM_CONSOLE_LOG: z.boolean().default(false),
  ENABLE_INFORMATION_BAR: z.boolean().default(false),
  STATSIG_CLIENT_KEY: z.string().default(''),
  // `[{ origin, pathPrefix }]`: another host for a CDN namespace the platform compiles in. An
  // unknown `pathPrefix` is dropped, so this never widens the paths validateRemoteUrl accepts
  // as script. configureCdnOrigins validates entries one by one: a malformed entry does not
  // discard the valid ones.
  ABAX_CDN_ORIGINS: z.array(z.unknown()).catch([]).default([]),
  // Loaded, unsigned, only while ABAX_UI_MANIFEST_URL is empty.
  ABAX_UI_REMOTE_URL: z.string().default('http://localhost:4001/remoteEntry.js'),
  // Signed @abax/ui manifest. A failed manifest load degrades to the consumer fallback; it never
  // falls back to ABAX_UI_REMOTE_URL.
  ABAX_UI_MANIFEST_URL: z.string().default(''),
  // Commit the signed manifest must name (CWE-829). A mismatch fails closed. A set pin also
  // bypasses the forward-only freshness floor, so it can freeze or roll back @abax/ui.
  ABAX_UI_EXPECTED_COMMIT: z.string().default(''),
  // Makes an empty ABAX_UI_EXPECTED_COMMIT fail the @abax/ui load. A runtime flag, not a
  // NODE_ENV check, so one production image can run with or without the requirement.
  ABAX_UI_REQUIRE_COMMIT_PIN: z.boolean().default(false),
  // Non-positive values deliberately select the runtime's default timeout.
  ABAX_UI_MANIFEST_TIMEOUT_MS: z.number().default(0),
});
